import React from "react";
import { motion } from "framer-motion";
import { ArrowDown, ArrowUpRight, Sparkle } from "@phosphor-icons/react";
import Button from "./Button";

const Hero = () => {
  return (
    <section
      className="relative pt-32 md:pt-36 pb-20 md:pb-28 overflow-hidden"
      data-testid="home-hero"
    >
      {/* Background image */}
      <div className="absolute inset-0 -z-10">
        <img
          src="https://images.pexels.com/photos/5884386/pexels-photo-5884386.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1300&w=1920"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-background/60 to-background dark:from-background/50 dark:via-background/75 dark:to-background" />
        <div className="absolute inset-0 bg-gradient-to-r from-background/80 via-background/30 to-transparent dark:from-background/90 dark:via-background/40" />
        <div className="absolute inset-0 grain" />
      </div>

      <div className="container relative">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="overline text-primary mb-6 flex items-center gap-2"
        >
          <Sparkle size={14} weight="fill" />
          <span>Energy · Infrastructure · Africa</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.05 }}
          className="font-display font-extrabold tracking-tight text-5xl sm:text-6xl md:text-7xl lg:text-[7.5rem] leading-[0.92] max-w-5xl"
        >
          Redefining
          <br />
          Africa's energy
          <br />
          <span className="text-primary">infrastructure.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-8 max-w-2xl text-base md:text-lg text-foreground/80 leading-relaxed"
        >
          Fradro Group is a diversified energy conglomerate engineering the
          pipelines, fuel supply and natural-gas networks that power Nigeria
          and the wider African market — with safety, integrity and
          long-horizon thinking at the core.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.35 }}
          className="mt-10 flex flex-wrap items-center gap-3"
        >
          <Button
            to="/services"
            size="lg"
            iconRight={ArrowUpRight}
            data-testid="hero-cta-services"
          >
            Explore our services
          </Button>
          <Button
            to="/contact"
            size="lg"
            variant="outline"
            data-testid="hero-cta-contact"
          >
            Start a project
          </Button>
        </motion.div>

        {/* Scroll cue + caption */}
        <div className="mt-20 md:mt-28 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="flex items-center gap-3 text-muted-foreground text-sm"
          >
            <span className="inline-block w-10 h-px bg-primary" />
            <span className="font-mono uppercase tracking-widest text-xs">
              Scroll to discover
            </span>
            <ArrowDown size={14} weight="bold" className="animate-bounce" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="grid grid-cols-2 sm:grid-cols-4 gap-6 md:gap-12 max-w-2xl"
          >
            {[
              { v: "80+", l: "Projects" },
              { v: "15+", l: "Partners" },
              { v: "153km", l: "Pipeline" },
              { v: "100%", l: "Satisfaction" },
            ].map((s) => (
              <div key={s.l} className="border-l border-border pl-4">
                <div className="font-display font-bold text-2xl md:text-3xl">
                  {s.v}
                </div>
                <div className="overline text-muted-foreground mt-1">
                  {s.l}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
